<template>
  <v-container class="blog-detail-container">
    <div v-if="blogPost">
      <v-row class="mb-8">
        <v-col cols="12">
          <h1 class="blog-title">{{ blogPost.title }}</h1>
          <p class="blog-summary">
            {{ blogPost.summary }}
          </p>
          <p class="blog-content" v-if="blogPost.content">
            {{ blogPost.content }}
          </p>
        </v-col>
      </v-row>
      <v-row
        v-for="(section, idx) in blogPost.sections"
        :key="idx"
        class="mb-6"
      >
        <v-col cols="12">
          <h2 class="section-heading">{{ section.heading }}</h2>

          <h3 v-if="section.subheading" class="section-subheading">
            {{ section.subheading }}
          </h3>

          <p v-if="section.note" class="section-note">
            {{ section.note }}
          </p>

          <p v-if="section.intro" class="section-text">
            {{ section.intro }}
          </p>

          <p v-if="section.content" class="section-text">
            {{ section.content }}
          </p>

          <!-- Points list -->
          <ul v-if="section.points" class="custom-list">
            <li v-for="(point, i) in section.points" :key="i" class="list-item">
              <span>{{ point }}</span>
            </li>
          </ul>

          <!-- Why it matters -->
          <v-alert
            v-if="section.whyItMatters"
            color="#f46523"
            dark
            class="custom-alert mb-4"
          >
            <span class="alert-title">Why it matters:</span>
            <span class="alert-text">{{ section.whyItMatters }}</span>
          </v-alert>

          <!-- How to steps -->
          <div v-if="section.howTo" class="steps-container mb-4">
            <p class="steps-title">How to do it:</p>
            <ol class="custom-ordered-list">
              <li
                v-for="(step, i) in section.howTo"
                :key="i"
                class="ordered-list-item"
              >
                <span>{{ step }}</span>
              </li>
            </ol>
          </div>
          <div
            v-if="section.diySteps || section.diyAction"
            class="steps-container mb-4"
          >
            <p class="steps-title">DIY steps:</p>
            <ul class="custom-list">
              <li
                v-for="(step, i) in section.diySteps || section.diyAction"
                :key="i"
                class="list-item"
              >
                <span>{{ step }}</span>
              </li>
            </ul>
          </div>
          <div v-if="section.checks" class="checks-container mb-4">
            <p class="steps-title">What to check:</p>
            <ul class="custom-list">
              <li
                v-for="(check, i) in section.checks"
                :key="i"
                class="list-item"
              >
                <span class="check-issue">{{ check.issue }}</span>
                <span> = </span>
                <span class="check-cause">{{ check.cause }}</span>
              </li>
            </ul>
            <p v-if="section.action" class="section-text mt-3">
              {{ section.action }}
            </p>
          </div>
          <div v-if="section.clearance" class="clearance-container mb-4">
            <p class="steps-title">Minimum clearance required:</p>
            <ul class="custom-list">
              <li
                v-for="(item, i) in section.clearance"
                :key="i"
                class="list-item"
              >
                <span>{{ item }}</span>
              </li>
            </ul>
            <p v-if="section.benefit" class="section-text mt-3">
              {{ section.benefit }}
            </p>
          </div>
          <ul v-if="section.tips" class="custom-list mb-4">
            <li v-for="(tip, i) in section.tips" :key="i" class="list-item">
              <span>{{ tip }}</span>
            </li>
          </ul>
          <div v-if="section.services" class="services-container mb-4">
            <p class="steps-title">Oh Yes Services offers:</p>
            <ul class="custom-list">
              <li
                v-for="(service, i) in section.services"
                :key="i"
                class="list-item"
              >
                <span>{{ service }}</span>
              </li>
            </ul>
          </div>
          <v-alert
            v-if="section.tip"
            color="#28a745"
            dark
            class="custom-alert mb-4"
          >
            <span class="alert-title">Tip:</span>
            <span class="alert-text">{{ section.tip }}</span>
          </v-alert>

          <v-alert
            v-if="section.bonus"
            color="#28a745"
            dark
            class="custom-alert mb-4"
          >
            <span class="alert-title">Bonus:</span>
            <span class="alert-text">{{ section.bonus }}</span>
          </v-alert>

          <v-alert
            v-if="section.expertTip"
            color="#ffc107"
            dark
            class="custom-alert mb-4"
          >
            <span class="alert-title">Expert Tip:</span>
            <span class="alert-text">{{ section.expertTip }}</span>
          </v-alert>

          <v-divider class="section-divider"></v-divider>
        </v-col>
      </v-row>

      <!-- Summary Table -->
      <v-row v-if="blogPost.summaryTable" class="mb-8">
        <v-col cols="12">
          <h2 class="section-heading">Quick Summary Table</h2>
          <div class="table-wrapper">
            <v-simple-table class="custom-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="table-header">Task</th>
                    <th class="table-header">Frequency</th>
                    <th class="table-header">DIY-Friendly?</th>
                    <th class="table-header">Benefits</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in blogPost.summaryTable"
                    :key="idx"
                    class="table-row"
                  >
                    <td class="table-cell">
                      <span>{{ row.task }}</span>
                    </td>
                    <td class="table-cell">
                      <span>{{ row.frequency }}</span>
                    </td>
                    <td class="table-cell">
                      <span :class="row.diyFriendly ? 'badge-yes' : 'badge-no'">
                        {{ row.diyFriendly ? "✅ Yes" : "❌ No" }}
                      </span>
                    </td>
                    <td class="table-cell">
                      <span>{{ row.benefits }}</span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="blogPost.finalThought" class="mb-8">
        <v-col cols="12">
          <v-card class="final-thought-card">
            <v-card-text class="pa-6">
              <h2 class="final-thought-title">
                Final Thought: Smart Maintenance Saves Money
              </h2>
              <p class="final-thought-text">{{ blogPost.finalThought }}</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="blogPost.conclusion" class="mb-8">
        <v-col cols="12">
          <h2 class="section-heading">Conclusion</h2>
          <p class="section-text">
            {{ blogPost.conclusion.content || blogPost.conclusion.heading }}
          </p>
        </v-col>
      </v-row>
      <v-row v-if="blogPost.faqSection" class="mb-8">
        <v-col cols="12">
          <h2 class="section-heading">{{ blogPost.faqSection.heading }}</h2>
          <div
            v-for="(faq, idx) in blogPost.faqSection.faqs"
            :key="idx"
            class="faq-item"
          >
            <h3 class="faq-question">
              <span class="faq-number">{{ idx + 1 }}.</span>
              <span>{{ faq.question }}</span>
            </h3>
            <p class="faq-answer">{{ faq.answer }}</p>
          </div>
        </v-col>
      </v-row>
    </div>
    <v-row v-else>
      <v-col cols="12" class="text-center">
        <v-alert type="error" prominent>
          <span class="error-text">Blog post not found!</span>
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { blogData } from "../../data/blogData.js";
import { filterBlogsForRegion } from "@/utils/blogRegion";

export default {
  name: "BlogDetailedView",
  data() {
    return {
      drawer: false,
      blogTitle: this.$route.params.blogTitle,
      blogPost: null,
    };
  },
  created() {
    this.loadBlogPost();
  },
  methods: {
    normalize(str) {
      return decodeURIComponent(str || "")
        .replace(/’/g, "'")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    },

    loadBlogPost() {
      const routeTitle = this.normalize(this.blogTitle);

      const allBlogs = filterBlogsForRegion(
        Object.values(blogData),
        this.isUSRoute(),
      );

      const foundBlog = allBlogs.find(
        (blog) => this.normalize(blog.title) === routeTitle,
      );

      this.blogPost = foundBlog || null;

      console.log("Route title:", routeTitle);
      console.log("Found:", this.blogPost);
    },

    isUSRoute() {
      return (
        this.$route.meta?.location === "us" ||
        this.$route.path.startsWith("/us")
      );
    },
  },

  watch: {
    "$route.params.blogTitle": function (newTitle) {
      this.blogTitle = newTitle;
      this.loadBlogPost();
    },
  },
};
</script>

<style scoped>
.blog-detail-container {
  max-width: 1200px;
  background-color: #ffffff;
  padding: 40px 20px;
}

.blog-title {
  font-size: 36px;
  font-weight: 700;
  color: #000000;
  line-height: 1.3;
  margin-bottom: 20px;
}

.section-heading {
  font-size: 28px;
  font-weight: 700;
  color: #f46523;
  margin-bottom: 16px;
  margin-top: 8px;
}

.section-subheading {
  font-size: 22px;
  font-weight: 600;
  color: #000000;
  margin-bottom: 12px;
}

.blog-summary,
.blog-content,
.section-text {
  font-size: 16px;
  font-weight: 400;
  color: #333333;
  line-height: 1.8;
  margin-bottom: 16px;
}

.section-note {
  font-size: 15px;
  font-style: italic;
  color: #666666;
  margin-bottom: 12px;
}

.custom-list,
.custom-ordered-list {
  padding-left: 30px;
  margin-bottom: 16px;
}

.list-item,
.ordered-list-item {
  font-size: 16px;
  color: #333333;
  line-height: 1.8;
  margin-bottom: 12px;
}

.list-item span,
.ordered-list-item span {
  color: #333333;
}

.steps-container,
.checks-container,
.clearance-container,
.services-container {
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid #f46523;
}

.steps-title {
  font-size: 18px;
  font-weight: 600;
  color: #000000;
  margin-bottom: 12px;
}

.check-issue {
  font-weight: 600;
  color: #f46523;
}

.check-cause {
  color: #333333;
}

/* Alerts */
.custom-alert {
  border-radius: 8px;
  padding: 16px 20px !important;
}

.alert-title {
  font-weight: 700;
  font-size: 16px;
  display: block;
  margin-bottom: 8px;
}

.alert-text {
  font-size: 15px;
  font-weight: 400;
  line-height: 1.6;
}

.table-wrapper {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.custom-table {
  background-color: #ffffff;
}

.table-header {
  background-color: #f46523 !important;
  color: #ffffff !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  padding: 16px !important;
  text-align: left !important;
}

.table-row:nth-child(even) {
  background-color: #f9f9f9;
}

.table-row:hover {
  background-color: #fff3ee;
}

.table-cell {
  padding: 14px 16px !important;
}

.table-cell span {
  font-size: 15px;
  color: #333333;
  font-weight: 400;
}

.badge-yes,
.badge-no {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  display: inline-block;
}

.badge-yes {
  background-color: #d4edda;
  color: #155724;
}

.badge-no {
  background-color: #f8d7da;
  color: #721c24;
}

.final-thought-card {
  background: linear-gradient(135deg, #f46523 0%, #ff8247 100%);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(244, 101, 35, 0.3);
}

.final-thought-title {
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 16px;
}

.final-thought-text {
  font-size: 16px;
  font-weight: 400;
  color: #ffffff;
  line-height: 1.8;
  margin: 0;
}

.faq-item {
  margin-bottom: 24px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border-left: 4px solid #f46523;
}

.faq-question {
  font-size: 18px;
  font-weight: 600;
  color: #000000;
  margin-bottom: 12px;
}

.faq-number {
  color: #f46523;
  margin-right: 8px;
}

.faq-answer {
  font-size: 15px;
  color: #333333;
  line-height: 1.7;
  margin: 0;
}

.section-divider {
  margin: 32px 0;
  border-color: #e0e0e0;
}

.error-text {
  font-size: 18px;
  font-weight: 600;
  color: #721c24;
}

@media (max-width: 768px) {
  .blog-title {
    font-size: 28px;
  }

  .section-heading {
    font-size: 24px;
  }

  .blog-detail-container {
    padding: 24px 16px;
  }

  .table-header,
  .table-cell span {
    font-size: 14px;
  }
}
</style>
